import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AutoTopupSetting,
  GetAutoTopupSettingsResponse,
  UpsertAutoTopupRequest,
  UpsertAutoTopupResponse,
  TopUpProduct,
} from '@dreamai/types';
import api from '../../api';
import './auto-topup-section.scss';

interface AutoTopupSectionProps {
  topUpProducts: TopUpProduct[];
  hasSubscription: boolean;
}

const LIMIT_PRESETS = [20, 40, 60];

const AutoTopupSection: FC<AutoTopupSectionProps> = ({
  topUpProducts,
  hasSubscription,
}) => {
  const { getAccessTokenSilently } = useAuth0();

  const [settings, setSettings] = useState<AutoTopupSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Single shared limit state
  const [limit, setLimit] = useState<number | 'custom'>(20);
  const [customValue, setCustomValue] = useState('');
  const [customEditing, setCustomEditing] = useState(false);

  const aiTopUp = topUpProducts.find((t) => t.creditType === 'ai_time');
  const humanTopUp = topUpProducts.find((t) => t.creditType === 'human_time');

  const aiSetting = settings.find((s) => s.creditType === 'ai_time');
  const humanSetting = settings.find((s) => s.creditType === 'human_time');

  const aiEnabled = aiSetting?.enabled ?? false;
  const humanEnabled = humanSetting?.enabled ?? false;

  // Refs to avoid stale closures in callbacks
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const limitRef = useRef(limit);
  limitRef.current = limit;
  const customValueRef = useRef(customValue);
  customValueRef.current = customValue;

  const getCapValue = useCallback(() => {
    const l = limitRef.current;
    return l === 'custom' ? parseFloat(customValueRef.current) || 20 : l;
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get<GetAutoTopupSettingsResponse>(
        '/credits/auto-topup',
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSettings(res.data.settings);

      // Read shared cap from the first setting that has one
      const first = res.data.settings[0];
      if (first) {
        const cap = first.monthlyBudgetCap;
        if (LIMIT_PRESETS.includes(cap)) {
          setLimit(cap);
        } else {
          setLimit('custom');
          setCustomValue(String(cap));
          setCustomEditing(false);
        }
      }
    } catch (err) {
      console.error('Error fetching auto-topup settings:', err);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    if (hasSubscription) {
      fetchSettings();
    } else {
      setLoading(false);
    }
  }, [hasSubscription, fetchSettings]);

  const upsertSetting = useCallback(
    async (
      creditType: 'ai_time' | 'human_time',
      enabled: boolean,
      budgetCap: number,
    ) => {
      const product = creditType === 'ai_time' ? aiTopUp : humanTopUp;
      if (!product) return;

      setSaving(true);
      try {
        const token = await getAccessTokenSilently();
        const body: UpsertAutoTopupRequest = {
          creditType,
          enabled,
          monthlyBudgetCap: budgetCap,
          balanceThreshold: 1,
          topupPriceId: product.priceId,
          topupQuantity: creditType === 'ai_time' ? 5 : 1,
        };
        await api.put<UpsertAutoTopupResponse>('/credits/auto-topup', body, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Re-fetch to stay in sync (backend syncs cap across all rows)
        await fetchSettings();
      } catch (err) {
        console.error('Error saving auto-topup setting:', err);
      } finally {
        setSaving(false);
      }
    },
    [getAccessTokenSilently, aiTopUp, humanTopUp, fetchSettings],
  );

  const handleToggle = useCallback(
    (creditType: 'ai_time' | 'human_time') => {
      const current = settingsRef.current.find(
        (s) => s.creditType === creditType,
      );
      const nowEnabled = current?.enabled ?? false;
      upsertSetting(creditType, !nowEnabled, getCapValue());
    },
    [upsertSetting, getCapValue],
  );

  const handleLimitChange = useCallback(
    (value: number | 'custom') => {
      setLimit(value);
      if (value === 'custom') {
        setCustomEditing(true);
      } else {
        // Save via whichever credit type is enabled (backend syncs cap to all)
        const cur = settingsRef.current;
        const enabledSetting = cur.find((s) => s.enabled);
        if (enabledSetting) {
          upsertSetting(enabledSetting.creditType, true, value);
        }
      }
    },
    [upsertSetting],
  );

  const handleCustomSubmit = useCallback(() => {
    const val = parseFloat(customValueRef.current);
    if (val > 0) {
      setCustomEditing(false);
      const cur = settingsRef.current;
      const enabledSetting = cur.find((s) => s.enabled);
      if (enabledSetting) {
        upsertSetting(enabledSetting.creditType, true, val);
      }
    }
  }, [upsertSetting]);

  if (!hasSubscription || loading) return null;

  const formatIncrement = (product: TopUpProduct | undefined, step: number) => {
    if (!product) return '';
    const amount = product.unitAmount * step;
    const withVat = Math.round(amount * 1.2);
    const sym =
      product.currency === 'GBP'
        ? '£'
        : product.currency === 'USD'
          ? '$'
          : product.currency === 'EUR'
            ? '€'
            : `${product.currency} `;
    return `${sym}${withVat}`;
  };

  return (
    <div className='settings-section'>
      <div className='autotopup-header'>
        <h2 className='section-title'>Automatically top-up my account</h2>
        <p className='autotopup-subtitle'>
          Automatically top-up your account when you run low on credits. Set a
          limit to keep in control.
        </p>
      </div>

      <div className='autotopup-card'>
        <div className='autotopup-toggles'>
          {aiTopUp && (
            <div
              className={`autotopup-toggle-row${!aiEnabled ? ' disabled' : ''}`}
            >
              <div className='autotopup-toggle-info'>
                <p className='autotopup-toggle-label'>
                  Automatically top-up{' '}
                  <span className='credit-type-ai'>AI Time</span>
                </p>
                <p className='autotopup-toggle-desc'>
                  Topped up in {formatIncrement(aiTopUp, 5)} increments
                </p>
              </div>
              <button
                className={`toggle-switch${aiEnabled ? ' on' : ''}`}
                onClick={() => handleToggle('ai_time')}
                disabled={saving}
              >
                <span className='toggle-knob' />
              </button>
            </div>
          )}

          {humanTopUp && (
            <div
              className={`autotopup-toggle-row${!humanEnabled ? ' disabled' : ''}`}
            >
              <div className='autotopup-toggle-info'>
                <p className='autotopup-toggle-label'>
                  Automatically top-up{' '}
                  <span className='credit-type-human'>Human Time</span>
                </p>
                <p className='autotopup-toggle-desc'>
                  Topped up in {formatIncrement(humanTopUp, 1)} increments
                </p>
              </div>
              <button
                className={`toggle-switch${humanEnabled ? ' on' : ''}`}
                onClick={() => handleToggle('human_time')}
                disabled={saving}
              >
                <span className='toggle-knob' />
              </button>
            </div>
          )}
        </div>

        {(aiEnabled || humanEnabled) && (
          <div className='autotopup-limits'>
            <div className='autotopup-limit-row'>
              <span className='autotopup-limit-label'>
                Monthly top-up limit
              </span>
              <div className='autotopup-radio-options'>
                {LIMIT_PRESETS.map((preset) => (
                  <button
                    key={preset}
                    className={`radio-option${
                      limit === preset ? ' selected' : ''
                    }`}
                    onClick={() => handleLimitChange(preset)}
                    disabled={saving}
                  >
                    £{preset}
                  </button>
                ))}
                {limit === 'custom' && customEditing ? (
                  <div className='radio-option custom-input selected'>
                    <span className='custom-prefix'>£</span>
                    <input
                      type='number'
                      value={customValue}
                      onChange={(e) => setCustomValue(e.target.value)}
                      onBlur={handleCustomSubmit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCustomSubmit();
                      }}
                      autoFocus
                      min='1'
                    />
                  </div>
                ) : limit === 'custom' && customValue ? (
                  <button
                    className='radio-option selected custom-confirmed'
                    onClick={() => setCustomEditing(true)}
                    disabled={saving}
                  >
                    £{customValue}
                  </button>
                ) : (
                  <button
                    className='radio-option'
                    onClick={() => handleLimitChange('custom')}
                    disabled={saving}
                  >
                    Custom
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AutoTopupSection;
